import { Suspense, lazy, useEffect, useRef } from "react";
import Lenis from "lenis";
import { BrowserRouter, Routes, Route, useLocation, matchRoutes } from "react-router-dom";
import { AnimatePresence, motion, MotionConfig } from "framer-motion";
import "@/App.css";
import Manifesto from "@/components/Manifesto";
import Invitation from "@/components/home/Invitation";
import { VoidVignette, SubconsciousThreshold } from "@/components/home/VoidLayers";
import Waitlist from "@/components/Waitlist";
import Footer from "@/components/Footer";
import Threshold from "@/components/Threshold";
import TheCrossing from "@/components/TheCrossing";
import ClosingTestament from "@/components/ClosingTestament";
import LotusParadox from "@/components/LotusParadox";
import TheEngine from "@/components/TheEngine";
import SiteHeader from "@/components/SiteHeader";
import WhisperBar from "@/components/WhisperBar";
import AmbientDrone from "@/components/AmbientDrone";
import LightField from "@/components/LightField";
import SkipLink from "@/components/SkipLink";
import SpaLinks from "@/components/SpaLinks";
import ArrivalRitual from "@/components/ArrivalRitual";
import RouteBloom from "@/components/RouteBloom";
import HeldBreath from "@/components/HeldBreath";
import SampleLightbox from "@/components/SampleLightbox";
import AuthCallback from "@/components/AuthCallback";
import { AuthProvider } from "@/lib/auth";
import NotFound from "@/pages/NotFound";
import LumenFeature from "@/components/home/LumenFeature";
import { LUMEN_PUBLIC } from "@/lib/flags";
import { RoomStart } from "@/components/rooms/RoomStart";
import Rail from "@/components/rooms/Rail";
import { resumePending, markResumed } from "@/lib/thread";
import Aperture from "@/components/rooms/Aperture";
const TheSheet = lazy(() => import("@/components/sheet/TheSheet"));
const GlassRoom = lazy(() => import("@/components/rooms/glass/GlassRoom"));
const GlassRecord = lazy(() => import("@/components/rooms/glass/GlassRecord"));
const GlowRoom = lazy(() => import("@/components/rooms/glow/GlowRoom"));
const LaboratoryDoor = lazy(() => import("@/components/rooms/laboratory/LaboratoryDoor"));
const RecordRiver = lazy(() => import("@/components/rooms/creators/RecordRiver"));
const CreatorsDoors = lazy(() => import("@/components/rooms/creators/CreatorsDoors"));
import WindowMoment from "@/components/home/WindowMoment";
import Families from "@/components/home/Families";
import { ROUTES, ROUTE_PATHS } from "@/lib/routes";
import { useSeo, useJsonLd, ORGANIZATION_LD } from "@/lib/seo";
import { trackScrollDepth } from "@/lib/analytics";

// Emotion → understanding → proof → possibility → human story → action.
function Home({ onEnter }) {
  useJsonLd("organization", ORGANIZATION_LD);
  return (
    <>
      <VoidVignette />
      <RoomStart id="prismpixel" />
      <WindowMoment />
      <Suspense fallback={<div className="min-h-[60vh]" aria-busy="true" />}>
        <TheSheet />
      </Suspense>
      <SubconsciousThreshold />
      <Manifesto />
      <Invitation />
      <RoomStart id="glass" />
      <Suspense fallback={<div className="min-h-screen" aria-busy="true" />}>
        <GlassRoom />
        <GlassRecord />
      </Suspense>
      <RoomStart id="glow" />
      <Suspense fallback={<div className="min-h-screen" aria-busy="true" />}>
        <GlowRoom />
      </Suspense>
      {LUMEN_PUBLIC && <LumenFeature />}
      <Families />
      <RoomStart id="creators" />
      <Suspense fallback={<div className="min-h-screen" aria-busy="true" />}>
        <RecordRiver />
        <CreatorsDoors />
      </Suspense>
      <RoomStart id="laboratory" />
      <Suspense fallback={<div className="min-h-[60vh]" aria-busy="true" />}>
        <LaboratoryDoor />
      </Suspense>
      <Waitlist />
      <Footer />
      <Threshold />
      <TheCrossing />
      <ClosingTestament />
      <LotusParadox />
      <TheEngine />
    </>
  );
}

function scrollToHash(hash, lenis, { immediate = false } = {}) {
  const until = performance.now() + 4000;
  const id = decodeURIComponent(hash.slice(1));
  // Aim by absolute position after re-measuring: during a route change Lenis still holds the old page's limit.
  const aim = (el) => {
    const top = Math.max(0, el.getBoundingClientRect().top + window.scrollY - 96);
    const l = lenis || window.__lenis;
    if (l) { l.resize(); l.scrollTo(top, { duration: 1.6, force: true, immediate }); }
    else window.scrollTo({ top, behavior: immediate ? "auto" : "smooth" });
  };
  const attempt = () => {
    const el = document.getElementById(id);
    if (el) {
      aim(el);
      // Late layout (fetched records, lazy rooms) moves the target; re-aim until the visitor takes the wheel.
      let touched = false;
      const stop = () => { touched = true; };
      ["wheel", "touchstart", "keydown"].forEach((ev) => window.addEventListener(ev, stop, { passive: true, once: true }));
      [900, 2200].forEach((ms) => setTimeout(() => { const again = document.getElementById(id); if (again && !touched) aim(again); }, ms));
      return;
    }
    if (performance.now() < until) requestAnimationFrame(attempt);
  };
  attempt();
}

function ScrollToTop({ lenisRef }) {
  const { pathname, hash } = useLocation();
  // Only a real page change scrolls to top; query-string updates (studio, filters) keep the user's place.
  useEffect(() => {
    if (hash) {
      scrollToHash(hash, lenisRef.current);
      return;
    }
    const room = resumePending(pathname, hash);
    if (room) {
      scrollToHash(`#room-${room}`, lenisRef.current, { immediate: true });
      return;
    }
    if (lenisRef.current) lenisRef.current.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
  }, [pathname, hash, lenisRef]);
  // The resume happens once per page load; marked after the first effects have settled.
  useEffect(() => {
    const t = setTimeout(markResumed, 0);
    return () => clearTimeout(t);
  }, []);
  return null;
}

function AnimatedRoutes() {
  const location = useLocation();
  const notFound = !matchRoutes(ROUTE_PATHS, location);
  useSeo(location.pathname, notFound);
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0, transition: { duration: 0.4, ease: [0.4, 0, 1, 1] } }}
        transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10"
      >
        <Suspense fallback={<div className="min-h-screen" aria-busy="true" data-testid="route-loading" />}>
          <Routes location={location}>
            <Route path="/" element={<HomeRoute />} />
            {ROUTES.map(({ path, Component }) => (
              <Route key={path} path={path} element={<Component />} />
            ))}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </motion.div>
    </AnimatePresence>
  );
}

let enterGalleryFn = () => {};
function HomeRoute() {
  return <Home onEnter={enterGalleryFn} />;
}

function App() {
  const lenisRef = useRef(null);

  useEffect(() => {
    const fine = window.matchMedia("(pointer: fine)").matches;
    const lenis = new Lenis({
      lerp: fine ? 0.12 : 0.085,
      smoothWheel: true,
      wheelMultiplier: fine ? 1.0 : 0.9,
      touchMultiplier: 1.3,
      syncTouch: false,
    });
    lenisRef.current = lenis;
    window.__lenis = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    const cleanupScroll = trackScrollDepth();
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      cleanupScroll();
    };
  }, []);

  enterGalleryFn = () => {
    if (lenisRef.current) {
      lenisRef.current.scrollTo("#the-passage", { offset: -72, duration: 2.0 });
    } else {
      document.getElementById("the-passage")?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <MotionConfig reducedMotion="user">
      <div className="App">
        <BrowserRouter>
          <AuthProvider>
          <SkipLink />
          <SpaLinks />
          <ScrollToTop lenisRef={lenisRef} />
          <LightField />
          <RouteBloom />
          <ArrivalRitual />
          <WhisperBar />
          <SiteHeader />
          <Rail />
          <Aperture />
          <AmbientDrone />
          <HeldBreath />
          <SampleLightbox />
          <main id="main" tabIndex={-1} className="outline-none">
            <AnimatedRoutes />
          </main>
          </AuthProvider>
        </BrowserRouter>
      </div>
    </MotionConfig>
  );
}

export default App;
